import { useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

import { Button } from "components/ui/button";
import { Input } from "components/ui/input";

const formatCount = (value: number) => value.toLocaleString("pt-BR");

interface CpfPaginationFooterProps {
  page: number;
  pageCount: number;
  firstItemIndex: number;
  lastItemIndex: number;
  filteredCount: number;
  totalCount: number;
  onPageChange: (page: number) => void;
}

export function CpfPaginationFooter({
  page,
  pageCount,
  firstItemIndex,
  lastItemIndex,
  filteredCount,
  totalCount,
  onPageChange,
}: CpfPaginationFooterProps) {
  const [pageDraft, setPageDraft] = useState(String(page));

  useEffect(() => setPageDraft(String(page)), [page]);

  const commitDraft = () => {
    const parsed = Number(pageDraft);
    if (!Number.isInteger(parsed) || parsed < 1) {
      setPageDraft(String(page));
      return;
    }
    onPageChange(Math.min(parsed, pageCount));
  };

  const isFiltered = filteredCount !== totalCount;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
      <span>
        Mostrando {formatCount(firstItemIndex)}–{formatCount(lastItemIndex)} de{" "}
        {formatCount(filteredCount)}
        {isFiltered && ` (filtrados de ${formatCount(totalCount)})`}
      </span>

      <div className="flex items-center gap-1">
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label="Primeira página"
          className="h-8 w-8"
          disabled={page <= 1}
          onClick={() => onPageChange(1)}
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label="Página anterior"
          className="h-8 w-8"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <span className="flex items-center gap-2 px-1">
          <Input
            aria-label="Ir para a página"
            inputMode="numeric"
            className="h-8 w-16 text-center"
            value={pageDraft}
            onChange={(event) =>
              setPageDraft(event.target.value.replace(/\D/g, ""))
            }
            onBlur={commitDraft}
            onKeyDown={(event) => {
              if (event.key !== "Enter") return;
              event.preventDefault();
              commitDraft();
            }}
          />
          <span className="whitespace-nowrap" aria-live="polite">
            de {formatCount(pageCount)}
          </span>
        </span>

        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label="Próxima página"
          className="h-8 w-8"
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label="Última página"
          className="h-8 w-8"
          disabled={page >= pageCount}
          onClick={() => onPageChange(pageCount)}
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
